import z from "zod";

// "action" = a non-conversational step the agent performs (no Q&A memory).
// Keyword research is the first: the agent researches + the user reviews, and
// the step completes when the keyword_research jsonb is populated (or skipped).
export const TopicKinds = ["conversational", "action"] as const;
export type TopicKind = typeof TopicKinds[number];

export const TopicNames = ["idea", "audience", "solution", "keywordResearch", "socialProof"] as const;
export type TopicName = typeof TopicNames[number];

// Flow order. keywordResearch sits between solution and socialProof: we ground
// the campaign in real keywords once we understand the offer, before polishing
// proof. socialProof is the terminal topic — once it's answered or skipped the
// brainstorm is complete and the user can build.
export const BrainstormTopics = ["idea", "audience", "solution", "keywordResearch", "socialProof"] as const;
export const ConversationalTopics = ["idea", "audience", "solution", "socialProof"] as const;
export type ConversationalTopicName = typeof ConversationalTopics[number];
export const ActionTopics = ["keywordResearch"] as const;
export type ActionTopicName = typeof ActionTopics[number];

export const BrainstormIntentNames = ["help_me", "skip_topic", "do_the_rest"] as const;
export type BrainstormIntentName = typeof BrainstormIntentNames[number];

export const topicSchema = z.object({
    name: z.enum(TopicNames),
    kind: z.enum(TopicKinds),
    description: z.string(),
    placeholderText: z.string(),
    skippable: z.boolean(),
    hardcodedQuestion: z.string().optional(),
})

export type Topic = z.infer<typeof topicSchema>;

export const Topics: Record<TopicName, Topic> = {
    idea: {
        name: "idea",
        kind: "conversational",
        description: "The core business idea. What does the business do? What makes them different?",
        placeholderText: "I want to acquire leads, sell my product...",
        skippable: false,
        hardcodedQuestion: "Tell us about your business. More info → better outcomes.",
    },
    audience: {
        name: "audience",
        kind: "conversational",
        description: "The target audience. What are their pain points? What are their goals?",
        placeholderText: "My target audience is...",
        skippable: true,
    },
    solution: {
        name: "solution",
        kind: "conversational",
        description: "How does the user's business solve the audience's pain points, or help them reach their goals?",
        placeholderText: "My solution is...",
        skippable: true,
    },
    keywordResearch: {
        name: "keywordResearch",
        kind: "action",
        description: "Ground the campaign in real, ranked Google keywords. The agent researches keywords from the idea/audience/solution, surfaces them as a table for the user to accept/decline, and saves the kept set. Not a question — an action the agent performs and the user reviews.",
        placeholderText: "Review the keywords, or tell me what to adjust...",
        skippable: true,
    },
    socialProof: {
        name: "socialProof",
        kind: "conversational",
        description: "Social proof or testimonials to include on the landing page. Remember, anything can be social proof: the user's background, experience, beliefs, founder story, etc.",
        placeholderText: "My social proof is...",
        skippable: true,
    },
}

export const getTopic = (topicName: TopicName): Topic => {
    return Topics[topicName];
}

export const getAllTopics = (): Topic[] => {
    return Object.values(Topics);
}

export const topicsAndDescriptions = (topics: TopicName[]): string => {
    return topics.map((topic) => Topics[topic].description).join("\n\n");
}

export const MemoriesSchema = z.object({
    idea: z.string().optional().nullable(),
    audience: z.string().optional().nullable(),
    solution: z.string().optional().nullable(),
    socialProof: z.string().optional().nullable(),
})

export type MemoriesType = z.infer<typeof MemoriesSchema>;

/**
 * Memory fields in order, corresponding to conversational topics.
 * Used to calculate current question number.
 */
const MemoryFields: (keyof MemoriesType)[] = ["idea", "audience", "solution", "socialProof"];

/**
 * The user-facing numbered steps. Action topics (e.g. keywordResearch) are
 * agent-performed, not questions the user answers, so they are excluded from the
 * "Question N of M" counter. Inserting an action step therefore doesn't shift
 * the conversational numbering the frontend shows.
 */
export const NumberedTopics = BrainstormTopics.filter(
    (t) => Topics[t].kind !== "action"
) as TopicName[];

/**
 * Get the current question number based on completed memories.
 * - Question 1: idea is undefined
 * - Question 2: audience is undefined
 * - Question 3: solution is undefined
 * - Question 4: socialProof is undefined
 * - Once all memories are complete, the counter pins to the last question.
 */
export function getCurrentQuestionNumber(memories: MemoriesType | undefined | null): number {
    if (!memories) return 1;

    for (let i = 0; i < MemoryFields.length; i++) {
        const field = MemoryFields[i];
        if (!field) continue; // TypeScript guard for array access
        const value = memories[field];
        if (value === undefined || value === null) {
            return i + 1;
        }
    }

    // All conversational memories complete - pin to the last numbered step.
    return NumberedTopics.length;
}

/**
 * Get the question number for a specific topic.
 * Maps topic names to their corresponding question numbers (1-indexed).
 * Action topics aren't numbered; they report the prior numbered step.
 */
export function getQuestionNumberForTopic(topic: TopicName | undefined | null): number {
    if (!topic) return 1;
    const index = NumberedTopics.indexOf(topic);
    if (index !== -1) return index + 1;
    // Action topic (not numbered): fall back to its position in the full flow,
    // clamped to the prior numbered step so the badge doesn't jump.
    const flowIndex = BrainstormTopics.indexOf(topic);
    return flowIndex <= 0 ? 1 : flowIndex;
}

/**
 * Total number of brainstorm questions (numbered steps only).
 */
export const TotalQuestions = NumberedTopics.length;